import client from '../client';
import type { Order, OrderItem, CreateOrderInput, UpdateOrderInput, OrderStatus, PaymentStatus, ShippingStatus } from '../../types/order';
import type { ApiResponse, PaginationParams } from '../../types/api';

interface OrderListParams extends PaginationParams {
  status?: string;
  delivery?: string;
  paymentStatus?: string;
  shippingStatus?: string;
  source?: string;
  startDate?: string;
  endDate?: string;
  sort?: 'asc' | 'desc';
  search?: string;
}

/* ------------------------------------------------------------------ */
/*  DB → frontend field mapping                                       */
/* ------------------------------------------------------------------ */

const DELIVERY_TO_SHIPPING: Record<string, ShippingStatus> = {
  confirming: 'pending',
  confirmed: 'pending',
  delivering: 'shipped',
  delivered: 'delivered',
  returned: 'failed',
  cancelled: 'failed',
  not_shipped: 'not_shipped',
  pending: 'pending',
  shipped: 'shipped',
  in_transit: 'in_transit',
  failed: 'failed',
};

const STATUS_TO_ORDER: Record<string, OrderStatus> = {
  payed: 'completed',
  pending: 'pending',
  processing: 'processing',
  completed: 'completed',
  cancelled: 'cancelled',
  refunded: 'refunded',
  failed: 'failed',
};

const STATUS_TO_PAYMENT: Record<string, PaymentStatus> = {
  payed: 'paid',
  paid: 'paid',
  pending: 'pending',
  failed: 'failed',
  refunded: 'refunded',
  partially_refunded: 'partially_refunded',
};

function transformOrder(raw: any, customerData?: any): Order {
  // Prefer the API's already-resolved `customer`: it reconciles the order's
  // clientInfo against the buyer's account and formats the phone server-side.
  // Raw `clientInfo` is only a fallback for older payloads.
  const ci = customerData || raw.customer || raw.clientInfo;
  const customer = ci
    ? {
        name: ci.name || ci.realName,
        email: ci.email,
        // Join with a template literal, never `+`: both parts are numbers in the
        // database, so `ci.phoneCountryCode + ci.phoneNumber` would add them.
        phone: ci.phoneCountryCode
          ? `+${ci.phoneCountryCode}${ci.phoneNumber || ''}`
          : ci.phone || (ci.phoneNumber ? String(ci.phoneNumber) : undefined),
        userId: raw.userId?.toString?.() || raw.userId,
      }
    : undefined;

  const items: OrderItem[] = (raw.items || []).map((it: any) => ({
    productId: it.productId?.toString?.() || it.productId || '',
    title: it.title || it.name || '',
    price: it.price || 0,
    quantity: it.quantity || it.defaultQuantity || 1,
    total: it.total || (it.price || 0) * (it.quantity || 1),
    image: it.image || it.product?.previewImage,
    sku: it.sku,
    variantTitle: it.variantTitle,
    fields: it.fields,
    productType: it.productType || it.product?.productType || null,
  }));

  // Prefer pre-transformed fields from the API; fall back to raw DB fields
  const dbStatus = raw.status || '';
  const dbPayment = raw.paymentStatus || dbStatus;
  const dbDelivery = raw.shippingStatus || raw.delivery || '';

  return {
    _id: raw._id?.toString?.() || raw._id || '',
    orderNumber: raw.orderNumber || raw.orderId,
    storeId: raw.storeId?.toString?.() || raw.storeId || '',
    userId: raw.userId?.toString?.() || raw.userId,
    customer,
    items,
    subtotal: raw.subtotal || items.reduce((s, it) => s + (it.total || 0), 0) || raw.amount || 0,
    total: raw.total || raw.amount || 0,
    discount: raw.discount,
    shippingCost: raw.shippingCost,
    tax: raw.tax,
    currency: raw.currency || 'SAR',
    status: STATUS_TO_ORDER[dbStatus] || (dbStatus as OrderStatus) || 'pending',
    delivery: (raw.delivery as any) || undefined,
    paymentStatus: STATUS_TO_PAYMENT[dbPayment] || (dbPayment as PaymentStatus) || 'pending',
    shippingStatus: DELIVERY_TO_SHIPPING[dbDelivery] || (dbDelivery as ShippingStatus) || 'not_shipped',
    shippingAddress: raw.shippingAddress || raw.address,
    couponCode: raw.couponCode,
    notes: raw.notes,
    fieldsData: raw.fieldsData,
    metadata: raw.metadata,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    shippingLabel: raw.shippingLabel,
    trackingId: raw.trackingId,
    shippingOrderId: raw.shippingOrderId,
    shippingCompany: raw.shippingCompany,
    paymentMethod: raw.paymentMethod || null,
    bankTransferStatus: raw.bankTransferStatus || null,
    bankTransferReceipt: raw.bankTransferReceipt || null,
    sessionId: raw.sessionId || null,
    allItemsAreAutoDelivered: raw.allItemsAreAutoDelivered || false,
    seen: raw.seen,
  };
}

/* ------------------------------------------------------------------ */
/*  API methods                                                       */
/* ------------------------------------------------------------------ */

export const ordersApi = {
  list: async (storeId: string, params?: OrderListParams) => {
    const res = await client.get<ApiResponse<any>>(
      `/dashboard/${storeId}/orders`,
      { params },
    );
    const d = res.data.data!;
    return {
      orders: (d.orders || []).map((o: any) => transformOrder(o)),
      total: d.pagination?.total ?? d.total ?? 0,
      page: d.pagination?.page ?? d.page ?? 1,
      limit: d.pagination?.limit ?? d.limit ?? 50,
    };
  },

  get: async (storeId: string, orderId: string): Promise<Order> => {
    const res = await client.get<ApiResponse<any>>(`/dashboard/${storeId}/orders/${orderId}`);
    const d = res.data.data!;
    // API returns { order, customer } - unwrap and merge
    if (d.order) {
      return transformOrder(d.order, d.customer);
    }
    return transformOrder(d);
  },

  create: async (storeId: string, data: CreateOrderInput) => {
    const res = await client.post<ApiResponse<{ orderId: string; message: string }>>(`/dashboard/${storeId}/orders`, data);
    return res.data.data!;
  },

  update: async (storeId: string, orderId: string, data: UpdateOrderInput & { shippingStatus?: string }) => {
    const res = await client.patch<ApiResponse<{ message: string }>>(`/dashboard/${storeId}/orders/${orderId}`, data);
    return res.data.data!;
  },

  unseenCount: async (storeId: string): Promise<number> => {
    const res = await client.get<ApiResponse<{ count: number }>>(`/dashboard/${storeId}/orders/unseen-count`);
    return res.data.data?.count ?? 0;
  },

  /** Get order counts per delivery status in a single call */
  statusCounts: async (storeId: string, params?: Omit<OrderListParams, 'page' | 'limit' | 'delivery' | 'sort'>): Promise<Record<string, number>> => {
    const res = await client.get<ApiResponse<{ counts: Record<string, number> }>>(
      `/dashboard/${storeId}/orders/counts`,
      { params },
    );
    return res.data.data?.counts ?? {};
  },

  exportCsv: async (storeId: string, params?: { startDate?: string; endDate?: string; status?: string; delivery?: string; source?: string; search?: string; sort?: string }) => {
    const res = await client.post(`/dashboard/${storeId}/orders/export`, params, {
      responseType: 'blob',
    });
    return res.data as Blob;
  },

  downloadInvoice: async (storeId: string, orderId: string) => {
    const res = await client.post(`/dashboard/${storeId}/orders/${orderId}/invoice`, {}, {
      responseType: 'blob',
    });
    return res.data as Blob;
  },

  reviewBankTransfer: async (storeId: string, orderId: string, action: 'approve' | 'reject') => {
    const res = await client.post<ApiResponse<{ message: string }>>(
      `/dashboard/${storeId}/orders/${orderId}/bank-transfer-review`,
      { action },
    );
    return res.data.data!;
  },

  generateQuotation: async (storeId: string, data: { productIds: string[]; customerName?: string; validDays?: number; notes?: string }) => {
    const res = await client.post(`/dashboard/${storeId}/products/quotation`, data, { responseType: 'blob' });
    return res.data as Blob;
  },
};
